/**
 * Notification bell (G5.5) — the in-app channel's surface.
 *
 * Polls the unread count (PendingApprovalsBadge pattern) and opens a
 * popover of recent notifications; clicking one marks it read and follows
 * its action_url when present.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ActionList,
  Badge,
  Button,
  InlineStack,
  Popover,
  Spinner,
  Text,
} from '@shopify/polaris';
import { NotificationIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';

import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from '../services/notificationsApi';

export function NotificationBell({
  refreshInterval = 60000,
}: {
  refreshInterval?: number;
}) {
  const navigate = useNavigate();
  const [unread, setUnread] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[] | null>(null);

  const refreshCount = useCallback(async () => {
    try {
      setUnread(await fetchUnreadCount());
    } catch {
      // Count is decorative; API errors must not break the header.
    }
  }, []);

  useEffect(() => {
    refreshCount();
    if (!refreshInterval) return;
    const timer = setInterval(refreshCount, refreshInterval);
    return () => clearInterval(timer);
  }, [refreshCount, refreshInterval]);

  const openPopover = async () => {
    setOpen(true);
    setItems(null);
    try {
      const data = await fetchNotifications(20);
      setItems(data.notifications);
      setUnread(data.unread_count);
    } catch {
      setItems([]);
    }
  };

  const handleItemClick = async (item: NotificationItem) => {
    setOpen(false);
    try {
      if (item.status !== 'read') {
        await markNotificationRead(item.id);
        setUnread(current => Math.max(0, current - 1));
      }
    } catch {
      // Read-marking is best-effort; still follow the link.
    }
    if (item.action_url) {
      navigate(item.action_url);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setUnread(0);
      setItems(current =>
        current
          ? current.map(item => ({ ...item, status: 'read' }))
          : current
      );
    } catch {
      // Best-effort.
    }
  };

  const activator = (
    <Button
      icon={NotificationIcon}
      variant="tertiary"
      onClick={() => (open ? setOpen(false) : openPopover())}
      accessibilityLabel="Notifications"
    >
      {unread > 0 ? String(unread) : undefined}
    </Button>
  );

  return (
    <Popover
      active={open}
      activator={activator}
      onClose={() => setOpen(false)}
      preferredAlignment="right"
    >
      <Popover.Pane>
        <div style={{ minWidth: '320px', maxWidth: '400px' }}>
          <div style={{ padding: '12px 16px 0' }}>
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingSm">
                Notifications
              </Text>
              {unread > 0 && (
                <Button variant="plain" onClick={handleMarkAllRead}>
                  Mark all read
                </Button>
              )}
            </InlineStack>
          </div>
          {items === null && (
            <div style={{ padding: '16px' }}>
              <InlineStack align="center">
                <Spinner size="small" accessibilityLabel="Loading notifications" />
              </InlineStack>
            </div>
          )}
          {items !== null && items.length === 0 && (
            <div style={{ padding: '16px' }}>
              <Text as="p" tone="subdued" variant="bodySm">
                Nothing yet — agent activity will show up here.
              </Text>
            </div>
          )}
          {items !== null && items.length > 0 && (
            <ActionList
              items={items.map(item => ({
                content: item.title,
                helpText: item.message,
                suffix:
                  item.status !== 'read' ? (
                    <Badge tone="attention">New</Badge>
                  ) : undefined,
                onAction: () => handleItemClick(item),
              }))}
            />
          )}
        </div>
      </Popover.Pane>
    </Popover>
  );
}
